// SubjectModal.js
import React, { useEffect, useState } from "react";
import {
  Modal,
  Icon,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  SimpleGrid,
  CheckboxGroup,
  Select,
  Stack,
} from "@chakra-ui/react";
import axios from "axios";
import {
  FaChalkboard,
  FaEdit,
  FaBuilding,
  FaLayerGroup,
  FaUserMinus,
  FaUserPlus,
} from "react-icons/fa";
import { BASE_URL } from "../../Globalapi/Globalapi";

function SubjectModal({
  isOpen,
  onClose,
  isEditMode,
  selectedClass,
  selectedSubjects,
  onSuccess,
}) {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [userType, setUserType] = useState(null);
  const [jwtToken, setJwtToken] = useState(null);

  useEffect(() => {
    const storedUserType = sessionStorage.getItem("user_type");
    const token = sessionStorage.getItem("jwt");
    setUserType(storedUserType);
    setJwtToken(token);
  }, []);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${jwtToken}` };

    axios
      .get(`${BASE_URL}/admin/class/class_master.php`, {
        headers,
      })
      .then((response) => {
        setClasses(response.data.data || []);
      })
      .catch((error) => console.error("Error fetching classes:", error));

    if (userType === "1") {
      axios
        .get(`${BASE_URL}/admin/subject/list_subject.php`, {
          headers,
        })
        .then((response) => {
          setSubjects(response.data.data || []);
        })
        .catch((error) => console.error("Error fetching subjects:", error));
    }
  }, [userType, jwtToken]);

  useEffect(() => {
    if (isEditMode && selectedClass) {
      setSelectedClassId(selectedClass);

      // Fetch the details for the selected class when in edit mode
      const fetchClassDetails = async () => {
        try {
          const response = await axios.get(
            `${BASE_URL}/admin/subject/selected_subjects.php?class_id=${selectedClass}`,
            { headers: { Authorization: `Bearer ${jwtToken}` } }
          );

          console.log(response.data); // Log the entire response for debugging

          // Check if the response contains selectedSubjects
          if (response.data.success && response.data.selectedSubjects) {
            // Set the selected subjects from the response
            setSelectedSubjectIds(response.data.selectedSubjects || []);
          } else {
            console.error("No selected subjects found in the response");
          }
        } catch (error) {
          console.error("Error fetching class details:", error);
        }
      };

      fetchClassDetails();
    } else {
      // Reset the selected subject IDs when not in edit mode
      setSelectedSubjectIds([]);
    }
  }, [isEditMode, selectedClass, jwtToken]);

  const handleSubmit = () => {
    const payload = {
      class_id: selectedClassId,
      subject_ids: selectedSubjectIds.filter((id) => id),
      pass: sessionStorage.getItem("user_id"),
    };

    const apiUrl = isEditMode
      ? `${BASE_URL}/admin/subject/edit_subject_to_class.php`
      : `${BASE_URL}/admin/subject/create_subject_to_class.php`;

    const method = isEditMode ? "PUT" : "POST";

    axios({
      method,
      url: apiUrl,
      data: payload,
      headers: { Authorization: `Bearer ${jwtToken}` },
    })
      .then((response) => {
        if (response.data.success) {
          alert(response.data.message);
          onSuccess(); // Call the callback to refresh the list
          onClose(); // Close the modal
        } else {
          alert("Failed to process the request");
        }
      })
      .catch((error) => console.error("Error submitting form:", error));
  };

  return (
    <Modal
      isCentered  
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        borderRadius="lg"
        boxShadow="xl"
        bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
        color="gray.700"
        p={6}
        maxW="700px"
        maxH="550px"
        overflowY="auto"
      >
        <ModalHeader
          fontSize="xl"
          fontWeight="bold"
          textAlign="center"
          color="teal.600"
        >
          <Icon as={FaEdit} boxSize={5} mr={2} />
          {isEditMode ? "Edit Subject to Class" : "Add Subject to Class"}{" "}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          overflowY="auto"
          px={4}
          css={{
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "10px",
              backdropFilter: "blur(8px)",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(173, 216, 230, 0.6)",
              borderRadius: "10px",
              backdropFilter: "blur(8px)",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "rgba(173, 216, 230, 0.9)",
            },
          }}
        >
          <Select
            bg="white"
            _hover={{ borderColor: "teal.300" }}
            placeholder="Select Class"
            value={selectedClassId}
            onChange={(e) => setSelectedClassId(e.target.value)}
            mb={4}
          >
            {classes.length > 0 &&
              classes.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.class}
                </option>
              ))}
          </Select>

          {userType === "1" && (
            <CheckboxGroup
              value={selectedSubjectIds}
              onChange={setSelectedSubjectIds}
              colorScheme="teal"
              mt={4}
            >
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                {subjects.length > 0 &&
                  subjects.map((subject) => (
                    <Checkbox key={subject.id} value={subject.id}>
                      {subject.subject}
                    </Checkbox>
                  ))}
              </SimpleGrid>
            </CheckboxGroup>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            bgGradient="linear(to-r, teal.400, teal.600)"
            color="white"
            _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
            mr={3}
            onClick={handleSubmit}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
          {/* <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button> */}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default SubjectModal;
